import { Box, Chip, Typography } from '@mui/material';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { deleteCollection as deleteCollectionAction } from '../../actions/collections.actions';
import { AsyncActionStatus } from '../../actions/common.actionTypes';
import { palette, spaces, typography } from '../../assets/styles/themes/tokens';
import { routes } from '../../constants';
import { CollectionFormModalContext } from '../../state/CollectionFormModalProvider';
import { NotificationContext } from '../../state/NotificationContextProvider';
import type { Collection, CollectionProperties, Pagination } from '../../types/AppContent';
import type { IconName } from '../../utils/getMuiIcon';
import { hasNext } from '../../utils/pagination';
import timeSinceLastUpdate from '../../utils/timeSinceLastUpdate';
import useAction from '../../utils/useAction';
import useResponsivePadding from '../../utils/useResponsivePadding';
import useRouter from '../../utils/useRouter';
import CardDescription from '../cards/common/CardDescription/NewCardDescription';
import CustomMuiCard from '../cards/MuiCard/CustomMuiCard';
import ConfirmationDialog from '../Dialog/ConfirmationDialog';
import ItemsList from '../ItemsList';
import OwnCollectionOverviewEmptyState from './OwnCollectionOverviewEmptyState';

interface OwnCollectionOverviewMainContentProps {
	collection: Collection;
	collectionStatus: AsyncActionStatus;
	collectionId: string;
	collectionPagination: Pagination;
	loadNext: () => void;
}

const OwnCollectionOverviewMainContent = ({
	collection,
	collectionStatus,
	collectionId,
	collectionPagination,
	loadNext,
}: OwnCollectionOverviewMainContentProps) => {
	const { t } = useTranslation();
	const cardPadding = useResponsivePadding();
	const [isExpanded, setIsExpanded] = useState(false);
	const [activeCollection, setActiveCollection] = useState<CollectionProperties | null>(null);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
	const deleteCollection = useAction(deleteCollectionAction);
	const { router } = useRouter();
	const { hideNotification, showNotification } = useContext(NotificationContext);
	const { show: openCollectionFormModal } = useContext(CollectionFormModalContext);
	const onClickBackButton = () => {
		router.push(`/${routes.collections.root}`);
	};

	const onClickEditButton = () => {
		hideNotification();
		openCollectionFormModal(collectionId, collection);
	};

	const chipsData = useMemo(
		() => [
			...(collection?.subjects || []),
			...(collection?.schoolStage ? [collection.schoolStage] : []),
		],
		[collection?.subjects, collection?.schoolStage]
	);

	const openDeleteDialog = (collection: CollectionProperties) => {
		setActiveCollection(collection);
		setIsDeleteDialogOpen(true);
	};

	const onConfirmAndClose = () => {
		if (activeCollection) {
			deleteCollection(activeCollection.id);
			showNotification({
				message: t('The collection {{name}} has been successfully deleted', {
					name: activeCollection.title,
				}),
				severity: 'success',
			});
			setActiveCollection(null);
		}
		setIsDeleteDialogOpen(false);
	};

	if (!collection && collectionStatus !== AsyncActionStatus.READY) return null;

	return (
		<Box sx={{ borderTop: `1px solid ${palette.elevation.outlined}` }}>
			<CustomMuiCard
				title={collection.title}
				subheader={`${collection.contents.length} ${t('Contents')}`}
				menuItems={[
					{
						label: t('Edit'),
						iconName: 'editCollection' as IconName,
						onClick: onClickEditButton,
						color: palette.text.primary,
					},
					{
						label: t('Remove'),
						iconName: 'trash' as IconName,
						onClick: () => openDeleteDialog(collection),
						color: palette.error.main,
					},
				]}
				color={collection.color}
				onClickBackButton={onClickBackButton}
				sxCardStyle={{
					main: {
						border: 'none',
						borderRadius: 'unset',
					},
					header: {
						padding: `${cardPadding} ${cardPadding} ${spaces.md} ${cardPadding}`,
					},
					title: {
						...typography.h5,
						color: palette.text.primary,
					},
					content: {
						padding: `${spaces.md} ${cardPadding} ${cardPadding} ${cardPadding}`,
						'&:last-child': { paddingBottom: cardPadding },
					},
				}}
				avatar="collectionsFolder"
			>
				<CardDescription
					description={collection.description}
					isExpanded={isExpanded}
					onShowToggleClick={() => setIsExpanded(!isExpanded)}
				/>
				<Box>
					{chipsData?.map((chip) => (
						<Chip
							key={chip}
							label={chip}
							variant="outlined"
							color="default"
							size="small"
							sx={{
								marginRight: spaces.xs,
								marginBottom: spaces.xs,
								border: `1px solid ${palette.components.chip.defaultEnabled}`,
								height: 'auto',
							}}
						/>
					))}
				</Box>
				<Typography variant="caption" sx={{ color: palette.text.primary }}>
					{timeSinceLastUpdate(collection.changed)} {t('Updated (date)')}
				</Typography>
			</CustomMuiCard>
			{collection.contentsCount === 0 ? (
				<OwnCollectionOverviewEmptyState />
			) : (
				<Box
					sx={{
						padding: `${0} ${cardPadding} ${cardPadding} ${cardPadding}`,
					}}
				>
					<ItemsList
						isLoading={collectionStatus === 'loading'}
						hasNext={hasNext(collectionPagination)}
						total={collectionPagination.total}
						loadNext={loadNext}
						items={collection.contents}
						linkRoot={[routes.followed.root, collectionId]}
					/>
				</Box>
			)}
			<ConfirmationDialog
				isOpen={isDeleteDialogOpen}
				title={t('Delete Collection')}
				message={t('Are you sure that you want to delete the collection')}
				onConfirm={onConfirmAndClose}
				onCloseModal={() => setIsDeleteDialogOpen(false)}
				confirmButtonText={t('Yes, remove')}
				confirmButtonColor="error"
			/>
		</Box>
	);
};

export default OwnCollectionOverviewMainContent;
